import { SupportedMimeType } from '../types/media.enums';

/**
 * Detect video MIME type from a URL by examining the file extension in the path.
 * Falls back to 'video/mp4' if extension is unrecognizable.
 */
export function detectVideoMimeType(url: string): string {
  try {
    const { pathname } = new URL(url);
    const ext = pathname.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'webm':
        return SupportedMimeType.VIDEO_WEBM;
      case 'mov':
        return 'video/quicktime';
      case 'mp4':
      default:
        return SupportedMimeType.VIDEO_MP4;
    }
  } catch {
    return SupportedMimeType.VIDEO_MP4;
  }
}

/**
 * File extension from a URL's PATH, lowercased, or undefined when there
 * isn't one.
 *
 * Parsing via `URL` is load-bearing: signed media URLs carry a query string
 * (and sometimes a `#t=` media fragment), so the naive
 * `url.split('.').pop().split('?')[0]` picks the last dot in the WHOLE string
 * rather than in the filename, and silently mis-detects whenever a query
 * value or a fragment contains a dot. TELV1-565.
 */
export function getMediaExtension(url: string): string | undefined {
  try {
    // The base only matters for relative inputs; absolute URLs ignore it.
    const { pathname } = new URL(url, 'https://media.invalid');
    const filename = pathname.split('/').pop() ?? '';
    if (!filename.includes('.')) return undefined;
    return filename.split('.').pop()?.toLowerCase() || undefined;
  } catch {
    return undefined;
  }
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'bmp'];
// `mov` matters: TELV1-554 added QuickTime uploads, and a viewer that doesn't
// recognise it renders neither an <img> nor a <video> — an empty modal.
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogv'];

export type MediaKind = 'image' | 'video' | 'unknown';

/**
 * Best-effort media kind from a URL. Prefer an explicit type from the backend
 * when the caller has one — this is the fallback for callers that don't.
 */
export function detectMediaKind(url: string): MediaKind {
  const ext = getMediaExtension(url);
  if (!ext) return 'unknown';
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return 'unknown';
}
